import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { fmtCur, fmtIDR } from "@/lib/format";
import { Trash2, AlertTriangle, Package, Pencil, Check, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CustomerSearch } from "@/components/CustomerSearch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function KursCell({ p, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(p.kurs);
  if (!canEdit) return <span className="font-mono">{Number(p.kurs).toLocaleString("id-ID")}{p.kurs_final && <Check className="inline w-3 h-3 text-emerald-600 ml-1" />}</span>;
  if (!editing) return (
    <button data-testid={`kurs-edit-${p.id.slice(0, 8)}`} onClick={() => { setV(p.kurs); setEditing(true); }} className="font-mono inline-flex items-center gap-1 hover:text-sky-700" title={p.kurs_final ? `Kurs final oleh ${p.kurs_updated_by}` : "Kurs sementara — klik untuk isi kurs tagihan"}>
      {Number(p.kurs).toLocaleString("id-ID")}{p.kurs_final ? <Check className="w-3 h-3 text-emerald-600" /> : <Pencil className="w-3 h-3 text-amber-500" />}
    </button>
  );
  return (
    <span className="inline-flex items-center gap-1">
      <Input data-testid={`kurs-input-${p.id.slice(0, 8)}`} type="number" step="any" value={v} onChange={(e) => setV(e.target.value)} className="h-7 w-24 font-mono" autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") { onSave(Number(v)); setEditing(false); } if (e.key === "Escape") setEditing(false); }} />
      <Button size="sm" className="h-7 px-2" data-testid={`kurs-save-${p.id.slice(0, 8)}`} onClick={() => { onSave(Number(v)); setEditing(false); }}>OK</Button>
    </span>
  );
}

export default function Bought() {
  const { event, eventId } = useOutletContext();
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [stock, setStock] = useState([]);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sell, setSell] = useState(null);
  const [sellForm, setSellForm] = useState({ customer_id: "", qty: 1, sell_price: "", note: "" });
  const canBuy = ["shopper", "admin"].some((r) => user.roles.includes(r));
  const canKurs = ["finance", "admin"].some((r) => user.roles.includes(r));
  const canSell = ["shopper", "sales", "admin"].some((r) => user.roles.includes(r));

  const load = async () => {
    const [p, s, ss, c] = await Promise.all([api.get(`/events/${eventId}/purchases`), api.get(`/events/${eventId}/stock`), api.get(`/events/${eventId}/stock-sales`), api.get("/customers")]);
    setPurchases(p.data); setStock(s.data); setSales(ss.data); setCustomers(c.data);
  };
  const saveKurs = async (p, kurs) => {
    if (!kurs || kurs <= 0) return toast.error("Kurs harus > 0");
    try { await api.patch(`/purchases/${p.id}/kurs`, { kurs }); toast.success(`${p.item_name}: kurs final disimpan`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const openSell = (s) => { setSell(s); setSellForm({ customer_id: "", qty: s.stock_qty, sell_price: "", note: "" }); };
  const saveSell = async () => {
    if (!sellForm.customer_id) return toast.error("Pilih customer");
    try {
      await api.post(`/events/${eventId}/stock-sales`, { catalog_item_id: sell.catalog_item_id, customer_id: sellForm.customer_id, qty: Number(sellForm.qty), sell_price: Number(sellForm.sell_price) || 0, note: sellForm.note });
      toast.success("Penjualan stok dicatat sebagai pendapatan event"); setSell(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delSale = async (s) => {
    if (!window.confirm(`Hapus penjualan ${s.name} x${s.qty} ke ${s.customer_name}?`)) return;
    try { await api.delete(`/stock-sales/${s.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (r) => {
    if (!window.confirm(`Hapus catatan belanja ${r.item_name} x${r.qty}? Alokasi ke customer akan dihitung ulang.`)) return;
    try { await api.delete(`/purchases/${r.id}`); toast.success("Catatan belanja dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const pCols = [
    { key: "created_at", label: "Waktu", render: (r) => new Date(r.created_at).toLocaleString("id-ID"), raw: (r) => r.created_at },
    { key: "item_name", label: "Item", render: (r) => <span className="font-semibold text-slate-900">{r.item_name}{r.sku && <span className="font-mono text-[10px] text-slate-400 ml-1">{r.sku}</span>}</span> },
    { key: "store", label: "Toko", render: (r) => r.store || <span className="text-slate-300">—</span> },
    { key: "qty", label: "Qty", mono: true, render: (r) => <span className="font-semibold">{r.qty}</span> },
    { key: "actual_cost", label: `Harga Beli (${event?.currency})`, mono: true, render: (r) => (
      <span className="flex items-center gap-1">{fmtCur(r.actual_cost, event?.currency)}{r.over_budget && <span title={`${r.over_pct}% di atas estimasi`} data-testid={`over-budget-${r.id.slice(0, 8)}`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded px-1"><AlertTriangle className="w-3 h-3" />+{r.over_pct}%</span>}</span>
    ), raw: (r) => r.actual_cost },
    { key: "kurs", label: "Kurs", mono: true, render: (r) => <KursCell p={r} canEdit={canKurs} onSave={(k) => saveKurs(r, k)} />, raw: (r) => r.kurs },
    { key: "cost_idr", label: "Total (IDR)", mono: true, render: (r) => <span className="font-semibold">{fmtIDR(r.cost_idr)}</span>, raw: (r) => r.cost_idr },
    { key: "payment_method", label: "Bayar", render: (r) => r.payment_method === "card" ? "Kartu" : "Cash" },
    { key: "note", label: "Catatan", render: (r) => r.note || <span className="text-slate-300">—</span> },
    { key: "created_by_name", label: "Shopper" },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => canBuy && (
      <button className="p-1.5 text-rose-400 hover:text-rose-600" data-testid={`del-purchase-${r.id.slice(0, 8)}`} onClick={() => remove(r)}><Trash2 className="w-4 h-4" /></button>
    ) },
  ];
  const sCols = [
    { key: "name", label: "Item", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
    { key: "sku", label: "SKU", mono: true },
    { key: "purchased_qty", label: "Dibeli", mono: true },
    { key: "allocated_qty", label: "Teralokasi", mono: true },
    { key: "stock_qty", label: "Stok", mono: true, render: (r) => <span className="font-bold text-amber-700">{r.stock_qty}</span> },
    { key: "avg_cost_idr", label: "Modal Rata-rata", mono: true, render: (r) => fmtIDR(Math.round(r.avg_cost_idr)), raw: (r) => Math.round(r.avg_cost_idr) },
    { key: "stock_value_idr", label: "Nilai Stok", mono: true, render: (r) => <span className="font-semibold">{fmtIDR(r.stock_value_idr)}</span> },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => canSell && (
      <Button size="sm" variant="outline" className="h-8 gap-1" data-testid={`sell-stock-${r.sku || r.catalog_item_id.slice(0, 6)}`} onClick={() => openSell(r)}><Tag className="w-3.5 h-3.5" /> Jual</Button>
    ) },
  ];
  const saleCols = [
    { key: "created_at", label: "Waktu", render: (r) => new Date(r.created_at).toLocaleString("id-ID"), raw: (r) => r.created_at },
    { key: "name", label: "Item", render: (r) => <span className="font-semibold text-slate-900">{r.name}</span> },
    { key: "customer_name", label: "Customer" },
    { key: "qty", label: "Qty", mono: true },
    { key: "sell_price", label: "Harga Jual", mono: true, render: (r) => fmtIDR(r.sell_price), raw: (r) => r.sell_price },
    { key: "total", label: "Total", mono: true, render: (r) => <span className="font-semibold text-emerald-700">{fmtIDR(r.total)}</span>, raw: (r) => r.total },
    { key: "created_by_name", label: "Oleh" },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => canSell && <button data-testid={`del-sale-${r.id.slice(0, 8)}`} className="p-1.5 text-rose-400 hover:text-rose-600" onClick={() => delSale(r)}><Trash2 className="w-4 h-4" /></button> },
  ];

  const totalIdr = purchases.reduce((s, p) => s + (p.cost_idr || 0), 0);
  const totalQty = purchases.reduce((s, p) => s + (p.qty || 0), 0);
  const stockValue = stock.reduce((s, r) => s + r.stock_value_idr, 0);

  return (
    <div className="space-y-8">
      <div>
        <PageHeader title="Bought — Riwayat Belanja" subtitle={`${purchases.length} transaksi · ${totalQty} pcs · total modal aktual ${fmtIDR(totalIdr)}. Setiap belanja otomatis dialokasikan ke order lunas (paling awal dulu); kelebihan menjadi stok.`} />
        <DataTable testId="purchases-table" columns={pCols} data={purchases} searchKeys={["item_name", "sku", "store", "created_by_name"]} exportName={`belanja-${event?.code}`} emptyText="Belum ada catatan belanja." />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-5 h-5 text-amber-600" />
          <h2 className="text-lg font-bold tracking-tight text-slate-900">Stok Sisa Event</h2>
          <span className="text-sm text-slate-500 ml-auto" data-testid="stock-total">{stock.reduce((s, r) => s + r.stock_qty, 0)} pcs · nilai <span className="font-mono font-semibold text-slate-900">{fmtIDR(stockValue)}</span></span>
        </div>
        <DataTable testId="stock-table" columns={sCols} data={stock} searchKeys={["name", "sku"]} exportName={`stok-${event?.code}`} emptyText="Tidak ada stok sisa — semua barang yang dibeli sudah teralokasi ke customer." />
      </div>
      {sales.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-lg font-bold tracking-tight text-slate-900">Penjualan Stok Sisa</h2>
            <span className="text-sm text-slate-500" data-testid="stock-sales-total">Pendapatan tambahan: <span className="font-mono font-semibold text-emerald-700">{fmtIDR(sales.reduce((s, r) => s + r.total, 0))}</span></span>
          </div>
          <DataTable testId="stock-sales-table" columns={saleCols} data={sales} searchKeys={["name", "customer_name"]} exportName={`jual-stok-${event?.code}`} />
        </div>
      )}

      <Dialog open={!!sell} onOpenChange={(v) => !v && setSell(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Jual Stok — {sell?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Customer</Label>
              <div className="mt-1"><CustomerSearch customers={customers} value={sellForm.customer_id} onSelect={(id) => setSellForm({ ...sellForm, customer_id: id })} onCreated={(c) => setCustomers((p) => [...p, c])} testId="sell-customer" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Qty (stok {sell?.stock_qty})</Label><Input data-testid="sell-qty" type="number" min="1" max={sell?.stock_qty} value={sellForm.qty} onChange={(e) => setSellForm({ ...sellForm, qty: e.target.value })} /></div>
              <div><Label>Harga Jual / pcs (IDR)</Label><Input data-testid="sell-price" type="number" min="0" step="1000" value={sellForm.sell_price} onChange={(e) => setSellForm({ ...sellForm, sell_price: e.target.value })} /></div>
            </div>
            <div><Label>Catatan</Label><Input data-testid="sell-note" value={sellForm.note} onChange={(e) => setSellForm({ ...sellForm, note: e.target.value })} /></div>
            <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm" data-testid="sell-total">Total: <span className="font-mono font-semibold">{fmtIDR((Number(sellForm.qty) || 0) * (Number(sellForm.sell_price) || 0))}</span> <span className="text-xs text-slate-400">· modal {fmtIDR(Math.round((sell?.avg_cost_idr || 0) * (Number(sellForm.qty) || 0)))}</span></div>
          </div>
          <DialogFooter><Button data-testid="sell-save" onClick={saveSell} className="bg-emerald-700 hover:bg-emerald-800">Catat Penjualan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
